import { type ReactElement, forwardRef, useMemo } from 'react';
import clsx from 'clsx';

import { Typography } from '@beeline/design-system-react/extracted-styles';

import { Image, ImageProps } from './Image';
import type { ImageMessageProps } from './types';

import { ButtonMessage } from './ButtonMessage';

import styles from './styles.module.scss';

export const ImageMessage = forwardRef<HTMLDivElement, ImageMessageProps>(
  (
    {
      PrimaryButton,
      SecondaryButton,
      className,
      buttonsContainerClassName,
      description,
      imageProps,
      imageSize,
      title,
      disablePadding = false,
      mobileReverseButtons = false,
      image,
    },
    ref,
  ): ReactElement => {
    const showButtons = PrimaryButton ?? SecondaryButton;

    const descriptionWrapper = useMemo(() => {
      const isTypography = typeof description === 'string';
      return isTypography ? (
        <Typography variant='body2' className={styles.description}>
          {description}
        </Typography>
      ) : (
        description
      );
    }, [description]);

    const imagePassProps: ImageProps = {
      imageProps,
      imageSize,
      ...image,
    };

    return (
      <div
        className={clsx(
          className,
          styles.imgMessage,
          !disablePadding && styles.padding,
        )}
        ref={ref}
      >
        <Image {...imagePassProps} />
        <div className={styles.textBlock}>
          <Typography variant='h6'>{title}</Typography>
          {description && descriptionWrapper}
        </div>

        {showButtons && (
          <div className={clsx(styles.buttons, buttonsContainerClassName, { [styles.mobileReverseButtons]: mobileReverseButtons })}>
            {SecondaryButton && (
              <ButtonMessage variant='outlined' {...SecondaryButton} />
            )}
            {PrimaryButton && (
              <ButtonMessage variant='contained' {...PrimaryButton} />
            )}
          </div>
        )}
      </div>
    );
  },
);

ImageMessage.displayName = 'ImageMessage';
